import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { ImageAction } from '../../../../types/qr.types';
import { ImagePickerModal } from '../../ImagePickerModal';
import { Button } from '../../../Button';
import { imagesApi } from '../../../../api/services';
import type { ImageItem } from '../../../../types/image.types';

interface ImageActionEditorProps {
  action: ImageAction;
  onChange: (action: ImageAction) => void;
}

export function ImageActionEditor({ action, onChange }: ImageActionEditorProps) {
  const [showPicker, setShowPicker] = useState(false);
  // Selecting an image only gives us its id to persist on the action, so keep the picked
  // ImageItem around locally for an instant thumbnail instead of waiting on the list query.
  const [pickedImage, setPickedImage] = useState<ImageItem | null>(null);

  // Fetched directly by id (rather than pulled out of a paginated list) so this can't miss the
  // image just because it isn't within the first page, and can distinguish "still loading" from
  // "this image no longer exists" (getImage 404s if it was deleted).
  const { data: fetchedImage, isLoading, isError } = useQuery({
    queryKey: ['image', action.image_id],
    queryFn: () => imagesApi.getImage(action.image_id),
    enabled: !!action.image_id && pickedImage?.id !== action.image_id,
    retry: false,
  });

  const image = pickedImage?.id === action.image_id ? pickedImage : fetchedImage;

  const handleSelect = (selected: ImageItem) => {
    onChange({ type: 'image', image_id: selected.id });
    setPickedImage(selected);
    setShowPicker(false);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[--clinqr-text-secondary]">Image destination</label>
      {action.image_id ? (
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 bg-[--clinqr-bg-subtle] rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
            {image ? (
              <img src={image.url} alt={image.filename} className="w-full h-full object-contain" />
            ) : isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-[--clinqr-text-secondary]" />
            ) : isError ? (
              <span className="text-[10px] text-[--clinqr-text-secondary] text-center px-1">Image deleted</span>
            ) : (
              <span className="text-xs text-[--clinqr-text-secondary]">#{action.image_id}</span>
            )}
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowPicker(true)}>
            Change image
          </Button>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setShowPicker(true)}>
          Choose image
        </Button>
      )}
      <p className="text-sm text-[--clinqr-text-muted]">
        When this rule matches, users will see this image instead of being redirected to a URL.
      </p>

      {showPicker && (
        <ImagePickerModal onSelect={handleSelect} onClose={() => setShowPicker(false)} />
      )}
    </div>
  );
}
